import type { MaterialStatus } from '@mnd-frontend/graphql-schema';
import {
  Badge,
  Button,
  Card,
  Icons,
  Panel,
  ScreenReaderOnly,
  Text,
  Tooltip,
} from '@mnd-frontend/ui/publish/components';
import {
  FOCUS_SHADOW,
  RED_700,
  SPACING_0_5,
  SPACING_1,
  SPACING_2,
  SPACING_3,
  SPACING_4,
} from '@mnd-frontend/ui/shared/tokens';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import truncateSentence from '../../lib/truncateSentence';
import { ImageStatusPill } from '../../modules/publish/components/ImageBankPage/components/ImageStatusPill';
import MediaCardView from './MediaCardView';

const StyledCheck = styled(Icons.Check)`
  color: white;
`;
const UnstyledButton = styled.button<{ $selectMode?: boolean }>`
  all: unset;
  width: 100%;
  height: 100%;
  cursor: pointer;
  > div {
    width: 100%;
    height: 100%;
    min-width: 12.8125rem;
    min-height: 12.8125rem;
  }
  ${({ $selectMode }) => $selectMode && 'cursor: pointer;'}
`;
const UnstyledDiv = styled.div`
  width: 100%;
  height: 100%;
  > div {
    width: 100%;
    height: 100%;
    min-width: 12.8125rem;
    min-height: 12.8125rem;
  }
`;

const StatusWrapper = styled.div`
  margin-top: ${SPACING_1};
  display: flex;
  gap: ${SPACING_0_5};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${SPACING_1};

  button,
  a {
    width: ${SPACING_4};
    height: ${SPACING_4};
    > div > span > i {
      width: ${SPACING_2};
      height: ${SPACING_2};
    }
  }
`;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${SPACING_1};

  > button {
    background-color: rgba(79, 78, 101, 0.5);
    position: absolute;
    top: ${SPACING_1};
    right: ${SPACING_1};
    width: ${SPACING_4};
    height: ${SPACING_4};
    > div > span > i {
      width: ${SPACING_2};
      height: ${SPACING_2};
    }
  }
`;
const Overlay = styled.div<{ $hover: boolean; $selectMode: boolean; $checked: boolean }>`
  display: ${props => (props.$hover || props.$checked ? 'flex' : 'none')};
  flex-direction: column;
  justify-content: ${props => (props.$checked ? 'center' : 'space-between')};
  align-items: ${props => (props.$checked ? 'center' : 'normal')};
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  padding: ${SPACING_3};
  background: ${props =>
    !props.$hover && props.$checked ? 'rgba(54, 54, 69, 0.50)' : 'rgba(54, 54, 69, 0.7)'};
  z-index: 2;
  border-radius: ${SPACING_1};

  > div > p {
    line-break: anywhere;
  }
`;

const GridItemStyles = styled.div<{ $isSelected: boolean; $isPending?: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  border-radius: ${SPACING_1};

  [data-whatintent='keyboard'] &:focus,
  [data-whatintent='keyboard'] &:focus-within {
    outline: none;
    z-index: 1;
    position: relative;
    box-shadow: ${FOCUS_SHADOW};
  }

  ${props =>
    props.$isSelected &&
    css`
      box-shadow: 0px 0px 0px 4px ${RED_700};
    `}

  ${({ $isPending }) =>
    $isPending &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
    `}

  ${Panel} {
    box-shadow: none;
    border-radius: ${SPACING_1};
    background-color: transparent;
  }
`;
type MediaCardProps = {
  title: string;
  image?: {
    url?: string;
  };
  status?: MaterialStatus;
  usageCount?: number;
  isSelected?: boolean;
  selectMode?: boolean;
  onClick?: () => void;
  isPending?: boolean;
  type?: string;
  editHref?: string;
  disabledEditText?: string;
  download?: string | null;
  onDelete?: () => void;
  onRemove?: () => void;
  noOverlay?: boolean;
};

const CardInner = ({
  selectMode,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  title,
  children,
}: {
  selectMode?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement | HTMLButtonElement>) => void;
  title: string;
  children: ReactNode;
}) => {
  if (selectMode) {
    return (
      <UnstyledButton
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
        <ScreenReaderOnly>{title}</ScreenReaderOnly>
      </UnstyledButton>
    );
  }
  return (
    <UnstyledDiv
      tabIndex={0}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {children}
    </UnstyledDiv>
  );
};

const MediaCard = ({
  title = '',
  image,
  status,
  usageCount,
  isSelected = false,
  selectMode = false,
  onClick,
  isPending = false,
  type,
  editHref,
  disabledEditText,
  download,
  onDelete,
  onRemove,
  noOverlay,
}: MediaCardProps) => {
  const [t] = useTranslation();
  const [hover, setHover] = useState(false);

  return (
    <GridItemStyles $isSelected={isSelected} $isPending={isPending} aria-busy={isPending}>
      <Card>
        <CardInner
          selectMode={selectMode}
          onClick={onClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setHover(true)}
          onBlur={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setHover(false);
            } // Only trigger onBlur if focus leaves the card completely
          }}
          title={title}
        >
          <MediaCardView type={type} image={image} noOverlay={noOverlay} />
          <Overlay $hover={hover} $selectMode={selectMode ?? false} $checked={isSelected}>
            {!selectMode && onRemove && (
              <RemoveButtonWrapper>
                <Button
                  type="button"
                  data-testid="image_card_remove_btn"
                  aria-label={t('common_remove')}
                  onClick={onRemove}
                  configuration="toned"
                  onDark
                  icon={<Icons.Close />}
                />
              </RemoveButtonWrapper>
            )}
            {!isSelected && (
              <div>
                <Text as="p" color="NEUTRAL_WHITE" variant="labelSmall">
                  {truncateSentence(title, 60)}
                </Text>
                <StatusWrapper>
                  {!!status && <ImageStatusPill status={status} showDraft />}
                  {usageCount ? (
                    <Tooltip content={t('media_bank_usage_tooltip', { count: usageCount })}>
                      <Badge
                        icon={<Icons.Story />}
                        variant="neutral"
                        text={usageCount.toString()}
                      />
                    </Tooltip>
                  ) : null}
                </StatusWrapper>
              </div>
            )}
            {!selectMode && !isSelected && (
              <ButtonsWrapper>
                {editHref && (
                  <Tooltip
                    content={disabledEditText ?? t('common_edit')}
                    disabled={!!disabledEditText}
                    placement={disabledEditText ? 'bottom' : undefined}
                  >
                    <Button
                      data-testid="image_card_edit_btn"
                      aria-label={t('common_edit')}
                      href={editHref}
                      disabled={!!disabledEditText}
                      configuration="toned"
                      onDark
                      icon={<Icons.Pen />}
                    />
                  </Tooltip>
                )}
                {download && (
                  <Tooltip content={t('common_download')}>
                    <Button
                      data-testid="image_card_download_btn"
                      aria-label={t('common_download')}
                      href={download}
                      configuration="toned"
                      onDark
                      icon={<Icons.Download />}
                    />
                  </Tooltip>
                )}
                {onDelete && (
                  <Tooltip content={t('common_delete')}>
                    <Button
                      type="button"
                      data-testid="image_card_delete_btn"
                      aria-label={t('common_delete')}
                      onClick={onDelete}
                      configuration="toned"
                      onDark
                      icon={<Icons.Delete />}
                    />
                  </Tooltip>
                )}
              </ButtonsWrapper>
            )}
            {selectMode && isSelected && <StyledCheck $size="lg" />}
          </Overlay>
        </CardInner>
      </Card>
    </GridItemStyles>
  );
};

export default MediaCard;
